/**
 * exifOrientation.js — decode a Blob into an ImageBitmap with EXIF orientation
 * baked into pixels and an optional resize applied in a single decode pass.
 *
 * FT-342 (2026-07-08) — capture pipeline.
 * See: Maritime-Agents/seaready-app/FT-340-photos/DESIGN-BRIEF.md §4.2 (stage 3).
 *
 * `createImageBitmap(blob, { imageOrientation: 'from-image', resizeWidth, ... })`
 * shipped in Safari 15 (Sep 2021), Chrome 79, Firefox 90. All targeted trial
 * devices have it; we fail loud if not.
 */

/**
 * Decode a blob to an ImageBitmap, applying EXIF orientation and resizing so
 * the LONG edge is at most `targetWidth` pixels. Aspect ratio is preserved.
 *
 * We use `resizeWidth: targetWidth` unconditionally because `createImageBitmap`
 * treats it as a "target width" hint. If the source is already smaller, the
 * bitmap comes back at source size — the resize is a cap, not a stretch.
 *
 * @param {Blob} blob         input image blob (must be a browser-decodable format)
 * @param {number} targetWidth long-edge cap in px (1920 or 2048)
 * @returns {Promise<ImageBitmap>}
 */
export async function decodeWithOrientation(blob, targetWidth) {
  if (!isBlobLike(blob)) {
    throw new Error('[exifOrientation] expected Blob input');
  }
  if (typeof createImageBitmap !== 'function') {
    // Safari < 15. Trial devices don't hit this; fail loud rather than silently
    // shipping unrotated pixels.
    throw new Error('[exifOrientation] createImageBitmap unavailable in this browser');
  }
  if (!Number.isFinite(targetWidth) || targetWidth <= 0) {
    throw new Error('[exifOrientation] targetWidth must be a positive number');
  }
  // `imageOrientation: 'from-image'` bakes EXIF orientation into pixels so
  // downstream stages (canvas encode, thumbnail) never see rotated data.
  return createImageBitmap(blob, {
    imageOrientation: 'from-image',
    resizeWidth: targetWidth,
    resizeQuality: 'high',
  });
}

function isBlobLike(v) {
  return v && typeof v === 'object' && typeof v.size === 'number' && typeof v.type === 'string' && typeof v.slice === 'function';
}

export default decodeWithOrientation;
